import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EAddressVerifiedStatus } from 'v2/shared/contact/address-verified-status.const';
import { IVerifiableAddressContact } from 'v2/shared/contact/address.interface';

import { CountryConfig, OrderApiService, StateConfig } from '../../../shared';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { trackById } from '../../../shared/util/track-by.util';

@Component({
  selector: 'lum-sales-order-recipient-form',
  templateUrl: './sales-order-recipient-form.component.html',
  styleUrls: ['./sales-order-recipient-form.component.scss'],
})
export class SalesOrderRecipientFormComponent implements OnChanges, OnDestroy {
  @Input()
  salesOrderId :number;

  @Input()
  data :IVerifiableAddressContact;

  @Output()
  updateRecipientForm = new EventEmitter<boolean>();

  trackById = trackById;
  private onDestroy$ = new Subject();

  form :UntypedFormGroup;
  toggleRecipientForm :boolean = false;
  street :string = '';
  countries :{ id :string; label :string }[] = [];
  states :{ id :string; label :string }[] = [];
  loading :boolean = false;

  constructor(
    private snackBar :MatSnackBar,
    private fb :UntypedFormBuilder,
    private cd :ChangeDetectorRef,
    private orderApiService :OrderApiService,
  ) {}

  ngOnChanges({ data } :SimpleChanges) :void {
    if (data && data.currentValue) {
      this.initializeComponent();
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  initializeComponent() :void {
    this.initCountries();
    this.initStates();
    this.initForm();
  }

  onSubmit() :void {
    this.loading = true;
    const payload = this.form.value;
    payload['updateShipTo'] = true;

    if (this.form.valid) {
      this.orderApiService
        .updateRowColumn(this.salesOrderId, payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :any) => {
            this.snackBar.open('Successfully updated ship to details.', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });

            this.loading = false;
            this.toggleRecipientForm = false;
            this.data = response.shipTo;
            this.initForm();
            this.updateRecipientForm.emit(true);
          },
          error: (err) => {
            this.loading = false;
            this.snackBar.open('Unable to update ship to details.', 'Error', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    }
  }

  onToggleEditRecipient() :void {
    this.toggleRecipientForm = !this.toggleRecipientForm;
  }

  private initForm() :void {
    this.form = this.fb.group({
      city: [this.data.city ?? null, Validators.required],
      company: [this.data.company ?? null],
      country: [this.data.country ?? null, Validators.required],
      name: [this.data.name ?? null, Validators.required],
      phone: [this.data.phone ?? null],
      postalCode: [this.data.postalCode ?? null, Validators.required],
      state: [this.data.state ?? null, Validators.required],
      street1: [this.data.street1 ?? null, Validators.required],
      street2: [this.data.street2 ?? null],
      street3: [this.data.street3 ?? null],
    });

    this.cd.detectChanges();
  }
  private initCountries() :void {
    const countries :any = CountryConfig.countries;

    this.countries = Object.keys(countries).map((code :any) => ({
      id: code,
      label: countries[code],
    }));
  }

  private initStates() :void {
    const states :any = StateConfig;

    this.states = Object.keys(states).map((code :any) => ({
      id: code,
      label: states[code],
    }));
  }

  get nameControl() :UntypedFormControl {
    return this.form.get('name') as UntypedFormControl;
  }

  get companyControl() :UntypedFormControl {
    return this.form.get('company') as UntypedFormControl;
  }

  get countryControl() :UntypedFormControl {
    return this.form.get('country') as UntypedFormControl;
  }

  get street1Control() :UntypedFormControl {
    return this.form.get('street1') as UntypedFormControl;
  }

  get street2Control() :UntypedFormControl {
    return this.form.get('street2') as UntypedFormControl;
  }

  get street3Control() :UntypedFormControl {
    return this.form.get('street3') as UntypedFormControl;
  }

  get cityControl() :UntypedFormControl {
    return this.form.get('city') as UntypedFormControl;
  }

  get stateControl() :UntypedFormControl {
    return this.form.get('state') as UntypedFormControl;
  }

  get postalCodeControl() :UntypedFormControl {
    return this.form.get('postalCode') as UntypedFormControl;
  }

  get phoneControl() :UntypedFormControl {
    return this.form.get('phone') as UntypedFormControl;
  }

  get addressValidationIcon() :string {
    let cellIcon = '';

    switch (this.data.addressVerified) {
      case EAddressVerifiedStatus.SUCCESS:
        if (!this.data.residential) {
          cellIcon = 'fa fa-building';
        } else {
          cellIcon = 'fa fa-home';
        }
        break;
      case EAddressVerifiedStatus.WARNING:
        cellIcon = 'fa fa-exclamation-triangle';
        break;
      case EAddressVerifiedStatus.FAILED:
        cellIcon = 'fa fa-exclamation-circle';
        break;
      case EAddressVerifiedStatus.NOT_FOUND:
      case EAddressVerifiedStatus.NOT_VERIFIED:
        cellIcon = 'fa fa-question-circle';
        break;
    }

    return cellIcon;
  }

  get addressValidationColor() :string {
    let cellClass = '';

    switch (this.data.addressVerified) {
      case EAddressVerifiedStatus.SUCCESS:
        cellClass = 'text-green-600 cell-address-green mr-2';
        break;
      case EAddressVerifiedStatus.WARNING:
        cellClass = 'text-orange-600 cell-address-orange mr-2';
        break;
      case EAddressVerifiedStatus.FAILED:
        cellClass = 'text-red-600 cell-address-red mr-2';
        break;
      case EAddressVerifiedStatus.NOT_FOUND:
      case EAddressVerifiedStatus.NOT_VERIFIED:
        cellClass = 'mr-2';
        break;
    }

    return cellClass;
  }
}
